import { cx, priceDisplay, useTranslations } from "@jugl-web/utils";

import { useMemo } from "react";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { getAllPrices } from "@jugl-web/domain-resources/invoices/utils";
import { InvoiceLevelDiscount } from "./components/InvoiceLevelDiscount";
import { InvoiceLevelTaxes } from "./components/InvoiceLevelTaxes";

const round = (num: number) => Math.round(num * 100) / 100;

export const ItemsFormTableFooter = () => {
  const { t } = useTranslations();
  const {
    currencySymbol,
    invoiceLevelDiscount,
    formValues,
    taxFields,
    selectedTaxes,
    itemsField,
  } = useInvoicesItemFormPageContext();
  const summaryItems = useMemo(() => {
    const { subtotal, total, invoiceTax, discount } = getAllPrices({
      items: itemsField.fields,
      discount: invoiceLevelDiscount,
      taxes: taxFields.invoice
        .filter((tax) => selectedTaxes.invoice.includes(tax.id))
        .map((tax) => ({
          percentage: tax.percentage,
        })),
    });
    const discountValue = discount ? round(discount) : 0;

    const invoiceTaxItems = taxFields.invoice
      .filter((tax) => selectedTaxes.invoice.includes(tax.id))
      .map((tax) => ({
        title: `${tax.name} (${tax.percentage}%)`,
        value: round((subtotal - discountValue) * (tax.percentage / 100)),
      }));

    return [
      {
        id: "subtotal",
        title: t({
          defaultMessage: "Subtotal",
          id: "invoices.subtotal",
        }),
        value: subtotal,
        prefix: currencySymbol,
        className: "text-sm",
      },
      {
        id: "discount",
        title: t({
          defaultMessage: "Invoice discount",
          id: "invoices.invoice-discount",
        }),
        value: discountValue,
        prefix: currencySymbol,
        suffix:
          discountValue > 0 && invoiceLevelDiscount.type === "percentage"
            ? `${invoiceLevelDiscount.amount}%`
            : "",
        className: "text-sm",
        component: InvoiceLevelDiscount,
      },
      {
        id: "total-vat",
        title: t({
          defaultMessage: "Invoice tax",
          id: "invoices.invoice-tax",
        }),
        prefix: currencySymbol,
        children: invoiceTaxItems,
        value: invoiceTax,
        className: "text-sm",
        component: InvoiceLevelTaxes,
      },
      {
        id: "total",
        title: t({
          defaultMessage: "Total",
          id: "invoices.total",
        }),
        prefix: currencySymbol,
        value: total,
        className: "text-base",
      },
    ];
  }, [
    itemsField.fields,
    selectedTaxes,
    taxFields,
    invoiceLevelDiscount,
    t,
    currencySymbol,
  ]);

  return (
    <ul className="mt-8 mb-0 min-w-[20rem] space-y-8 font-secondary">
      {summaryItems.map((item) => {
        const EditComponent = item.component;

        return (
          <li key={item.id} className="relative list-none">
            {item.children && (
              <ul className="mb-1 pl-0">
                {item.children.map((child) => (
                  <li
                    key={child.title}
                    className="flex list-none items-start justify-between gap-4 text-sm text-grey-700"
                  >
                    {child.title}
                    <div className="group flex min-w-[14rem] text-sm">
                      {priceDisplay(
                        (round(child.value) ?? 0) * 100,
                        formValues.currency,
                        undefined,
                        {
                          className: "flex gap-0.5 items-center",
                        }
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-start justify-between gap-4">
              <div
                className={cx(
                  "flex w-48 gap-1.5 text-dark-800",
                  item.className
                )}
              >
                {item.title}
                {item.suffix && (
                  <div className="text-sm text-grey-700">{item.suffix}</div>
                )}
              </div>
              <div className="group flex min-w-[14rem] items-center justify-between gap-1 text-sm">
                <div className="flex">
                  {item.id === "discount" && item.value > 0 ? (
                    <span>-</span>
                  ) : (
                    ""
                  )}
                  {priceDisplay(
                    (Number(item.value) ?? 0) * 100,
                    formValues.currency,
                    undefined,
                    {
                      currencyClassName: "text-[#828282]",
                      className: "flex gap-0.5 items-center",
                    }
                  )}
                </div>
              </div>
            </div>

            {EditComponent ? (
              <div className="absolute top-1/2 right-0 w-8 -translate-y-1/2">
                <EditComponent />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
